import { useState } from "react";
import { portfolio } from "@/mocks/portfolio";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Lightbox from "@/pages/portfolio/components/Lightbox";

const categories = ["All", "Exterior", "Interior", "Commercial", "Repair", "Pressure Washing"];

const getInitialCategory = (): string => {
  const params = new URLSearchParams(window.location.search);
  const cat = params.get("category");
  if (cat && categories.includes(cat)) return cat;
  return "All";
};

export default function Portfolio() {
  const [active, setActive] = useState(getInitialCategory);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(10);

  const filtered =
    active === "All"
      ? portfolio
      : portfolio.filter((p) => p.category === active);

  const displayed = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const handleCategoryChange = (category: string) => {
    setActive(category);
    setVisibleCount(10);
  };

  const loadMore = () => {
    setVisibleCount((prev) => prev + 10);
  };

  const openLightbox = (id: number) => {
    const idx = filtered.findIndex((p) => p.id === id);
    if (idx !== -1) setLightboxIndex(idx);
  };

  const closeLightbox = () => setLightboxIndex(null);

  const prevImage = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + displayed.length) % displayed.length);
  };

  const nextImage = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % displayed.length);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <section className="pt-28 pb-12 px-4 md:px-8 lg:px-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center border border-foreground-300 rounded-full px-5 py-1.5 text-xs font-medium tracking-wider uppercase text-foreground-600 mb-6">
                Our Work
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-950 mb-4">
                Project Portfolio
              </h1>
              <p className="text-foreground-600 max-w-lg mx-auto">
                A showcase of our completed painting projects across Southern California.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => handleCategoryChange(c)}
                  className={`px-4 py-2 rounded-full text-sm font-medium tracking-wider uppercase transition-all whitespace-nowrap cursor-pointer ${
                    active === c
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {displayed.map((project) => (
                <div
                  key={project.id}
                  onClick={() => openLightbox(project.id)}
                  className="group relative rounded-xl overflow-hidden aspect-[4/5] cursor-pointer"
                >
                  <img
                    src={project.image}
                    alt={project.title}
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="absolute top-3 right-3">
                    <span className="bg-accent-500 text-background-50 text-xs font-medium px-3 py-1 rounded-full">
                      {project.category}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="mt-10 text-center">
                <button
                  onClick={loadMore}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 text-sm font-medium tracking-wide uppercase transition-all whitespace-nowrap cursor-pointer"
                >
                  Load More
                  <span className="text-foreground-400 text-xs">
                    ({displayed.length} of {filtered.length})
                  </span>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      {lightboxIndex !== null && (
        <Lightbox
          images={displayed}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </div>
  );
}